// src/components/NotesSection.jsx
import { useState, useEffect, useCallback } from 'react';
import NoteItem from './NotesIndexNoteItem';
import Pagination from 'rc-pagination';
import PaginationLocale from 'rc-pagination/lib/locale/ko_KR';
import { Note } from 'types/notes';
import { useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { MyLoading } from 'components/atoms/MyLoading';
import { UserInfo } from 'types/user_info';
import getRootData from 'utils/getRootData';

interface RootData {
  userInfo: UserInfo;
  pboardName: string;
  pbid: string;
}

interface ApiResponse {
  items: Note[];
  page: number;
  total_count: number;
  per_page: number;
}

interface PboardApiResponse {
  id: number
  pbid: string;
  name: string;
  short_name: string;
  category: string;
}

interface NotesIndexProps {
  userInfo: UserInfo | null;
  pboard?: string;
}

// category_str to array of [key, value] pair
const parseCategorStr = (category_str: string): Array<[string, string]> => {
  let categories = category_str.split(',');
  categories = categories.filter((item) => item !== '');
  const categoriesArray: Array<[string, string]> = categories.map((category) => [category, category]);
  categoriesArray.unshift(['전체', '']);
  return categoriesArray;
};

const NotesIndex = ({ userInfo, pboard }: NotesIndexProps) => {
  const [loading, setLoading] = useState(true);
  const [notesLoading, setNotesLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageCount, setPageCount] = useState<number>(0);
  const itemsPerPage: number = 9; // Adjust based on how many notes you want per page
  const [totalCount, setTotalCount] = useState(0);
  const [currentNotes, setCurrentNotes] = useState<Note[]>([]); //useState(dummyNotes.slice(indexOfFirstNote, indexOfLastNote));
  const [searchParams, setSearchParams] = useSearchParams();
  const { pboardName: initialPboardName } = getRootData<RootData>() ?? { pboardName: '노트' };
  const [categories, setCategories] = useState<Array<[string, string]>>(new Array());
  const [query, setQuery] = useState<string>(searchParams.get('query') || '');
  const [pboardName, setPboardName] = useState<string>(initialPboardName);
  const isAdmin = userInfo?.roleinfo?.isAllAdminGroup;

  const fetchPboardInfo = useCallback(async (pbid: string) => {
    setLoading(true);

    try {
      const { data } = await axios.get<PboardApiResponse>('/api/v1/pboard?pbid=' + pbid);
      const categoryArr = parseCategorStr(data.category);
      setCategories(categoryArr);
      setPboardName(data.name);
      console.log('categoryArr', categoryArr);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  // const fetchNotes = useCallback(async (page: number, per_page: number, category: string) => {
  //   setLoading(true);

  //   try {
  //     // get items
  //     const params = new URLSearchParams({ page: page.toString(), per_page: per_page.toString(), category: category });
  //     const { data } = await axios.get<ApiResponse>(`/api/v1/notes?${params.toString()}`);
  //     setCurrentNotes(data.items);
  //     setTotalCount(data.total_count);
  //     setPageCount(Math.ceil(data.total_count / data.per_page));
  //   } catch (e) {
  //     console.error(e);
  //   } finally {
  //     setLoading(false);
  //   }
  // }, [categories, activeTab]);

  const fetchNotes = useCallback(async (page: number, per_page: number, category: string, query: string, pbid: string, tag?: string) => {
    setNotesLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: per_page.toString(),
        category: category,
        pboard: pbid,
      });
      if (query) {
        params.append('query', query);
      }
      if (tag) {
        params.append('tag', tag);
      }
      const { data } = await axios.get<ApiResponse>(`/api/v1/notes?${params.toString()}`);
      setCurrentNotes(data.items);
      setTotalCount(data.total_count);
      setPageCount(Math.ceil(data.total_count / data.per_page));
    } catch (e) {
      console.error(e);
    } finally {
      setNotesLoading(false);
    }
  }, []);

  useEffect(() => {
    const pbid = pboard || searchParams.get('pboard') || 'notes';
    fetchPboardInfo(pbid);
  }, [searchParams, fetchPboardInfo]);

  // useEffect(() => {
  //   const page = searchParams.get('page');
  //   page ? setCurrentPage(parseInt(page)) : setCurrentPage(1);
  // }, [searchParams]);

  useEffect(() => {
    const page = searchParams.get('page') || '1';
    const category = searchParams.get('category') || '';
    const q = searchParams.get('query') || '';
    const tag = searchParams.get('tag') || '';
    const pbid = pboard || searchParams.get('pboard') || 'notes';

    setCurrentPage(parseInt(page));
    setActiveTab(category);
    setQuery(q);

    // 태그 검색 시 fetchNotes 호출
    fetchNotes(parseInt(page), itemsPerPage, category, q, pbid, tag);
  }, [searchParams, fetchNotes]);

  // const onPageChange = (page: number) => {
  //   console.log('onPageChange event', page);
  //   if (page < 1) page = 1;
  //   else if (page > pageCount) page = pageCount;
  //   setCurrentPage(page);
  //   setSearchParams({ page: page.toString() });
  // };
  const onPageChange = (page: number) => {
    if (page < 1) page = 1;
    else if (page > pageCount) page = pageCount;

    setCurrentPage(page);
    setSearchParams({
      page: page.toString(),
      category: activeTab,
      query: query,
      pboard: searchParams.get('pboard') || 'notes',
    });
  };

  const onChangeCategory = (category: string) => {
    console.log('onChangeCategory event', category);
    setActiveTab(category);
    setCurrentPage(1);
      setSearchParams({ page: '1', category: category, query: query, pboard: pboard || searchParams.get('pboard') || 'notes' });
  }

  const handleSearch = () => {
      setSearchParams({
        page: '1',
        category: activeTab,
        query: query,
        pboard: pboard || searchParams.get('pboard') || 'notes',
      });
    setCurrentPage(1);
  };

  if (loading || notesLoading) {
    return <MyLoading />;
  }

  const currentTag = searchParams.get('tag');

  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl md:text-2xl font-bold">{pboardName}</h2>
        <div className='flex items-center'>
          <div className="form-control">
            <input
              type="text"
              value={query}
              placeholder="검색어를 입력하세요."
              className="input input-bordered w-32! md:w-64!"
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
            />
          </div>
          <button className="btn btn-secondary btn-sm ml-2" onClick={handleSearch}>검색</button>
        </div>
      </div>

      {/* 태그 검색 중일 때 표시 */}
      {currentTag && (
        <div className="mb-4 flex items-center gap-2">
          <span className="text-sm text-gray-600">태그 검색:</span>
          <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
            #{currentTag}
          </span>
          <button
            onClick={() => {
              setSearchParams({ page: '1', pboard: searchParams.get('pboard') || 'notes' });
            }}
            className="btn btn-ghost btn-xs"
          >
            ✕ 태그 검색 해제
          </button>
        </div>
      )}

      {categories.length > 1 && (
        <div role="tablist" className="tabs tabs-boxed mb-6">
          {categories.map(([label, value]) => (
            <button
              key={label}
              role="tab"
              className={`tab ${activeTab === value ? 'tab-active' : ''}`}
              onClick={() => onChangeCategory(value)}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8 mb-8">
        {currentNotes.map(note => ( // Use currentNotes for rendering
          <NoteItem key={note.id} note={note} pboard={pboard || searchParams.get('pboard') || 'notes'} />
        ))}
        {currentNotes.length === 0 &&
          (
            <>
              <div></div>
              <div className='flex justify-center px-4 py-30'>노트가 없습니다.</div>
            </>
          )
        }
      </div>

      <Pagination
        className="flex justify-center mt-8 gap-2 w-full join"
        total={totalCount}
        pageSize={itemsPerPage}
        current={currentPage}
        locale={PaginationLocale}
        showTitle={false}
        onChange={onPageChange}
        itemRender={(page, type, originalElement) => {
          if (type === 'page') {
            return (<button
              key={page}
              className={`join-item btn ${page === currentPage ? 'btn-active' : ''}`}
              onClick={() => onPageChange(page)}
            >
              {page}
            </button>);
          } else if (type === 'prev') {
            return (<button className="join-item btn" disabled={currentPage === 1}
              onClick={() => onPageChange(currentPage - 1)}>«</button>);
          } else if (type === 'next') {
            return (<button className="join-item btn" disabled={currentPage === pageCount}
              onClick={() => onPageChange(currentPage + 1)}>»</button>);
          }

          return originalElement;
        }}
      />

      {isAdmin &&
        <div className="float-right text-right mb-5">
          <div className='w-full flex justify-end max-h-16 overflow-hidden'>
            <a href={`/admin/pposts?pbid=${pboard || searchParams.get('pboard') || 'notes'}`}><img src='/img/common/admin.png' /></a>&nbsp;
          </div>
        </div>
      }
    </>
  );
};

export default NotesIndex;
